<script lang="ts" context="module">
    import Modal, { createModal } from '$components/Modal.svelte';

    export { createModal };
</script>

<script lang="ts">
    import {
        BookmarksQueryDocument,
        type BookmarkItemFragment,
        BookmarkFragmentDoc,
        EditBookmarkItemMutationDocument,
        BookmarkItemFragmentDoc,
    } from '$gql/graphql.js';
    import { melt } from '@melt-ui/svelte';
    import { getContainer } from '$components/Context/ContainerContext.js';
    import { cx } from '$lib/utils/cx.js';
    import { useFragment } from '$gql/fragment-masking.js';
    import { query } from '$lib/apollo/SvelteApollo.js';
    import { documentName } from '$gql/documentName.js';

    export let bookmarkItem: BookmarkItemFragment;
    export let modal: ReturnType<typeof createModal>;
    const {
        elements: { title, close },
        states: { open },
    } = modal;

    const apollo = getContainer().apollo();
    const {
        helpers: { addToast },
    } = getContainer().toaster();

    let bookmarkName = '';
    let loading = false;

    const { result } = query(BookmarksQueryDocument, {});

    $: bookmarks = ($result.data?.bookmarks ?? []).map((m) =>
        useFragment(BookmarkFragmentDoc, m),
    );

    async function edit({
        bookmarkName,
        bookmarkId,
    }: {
        bookmarkName?: string;
        bookmarkId?: number;
    }) {
        loading = true;
        const result = await apollo.mutate({
            mutation: EditBookmarkItemMutationDocument,
            variables: {
                bookmarkId: bookmarkId ?? null,
                bookmarkName: bookmarkName ?? null,
                id: bookmarkItem.id,
            },
            refetchQueries: [documentName(BookmarksQueryDocument)],
            awaitRefetchQueries: true,
        });

        loading = false;
        let item = useFragment(BookmarkItemFragmentDoc, result.data?.bookmark.item);
        if (item != null) {
            addToast({
                data: {
                    type: 'success',
                    message: 'Dossier de favoris mis à jour avec succès',
                },
            });
            $open = false;
        }
    }
</script>

<Modal contentClass="max-w-[900px] min-w-[600px]" dialogClass="p-0 items-start" {modal}>
    <div class="p-5 border-0">
        <button use:melt={$close} class="close">
            <span aria-hidden="true">&times;</span>
        </button>
        <h4 use:melt={$title} class="font-bold text-20 p-0 m-0 line-clamp-1 break-all">
            Mes favoris
        </h4>
    </div>

    <div class="text-left p-5" class:loading>
        <div class={'text-gray-600 my-4'}>
            Veuillez choisir ou créer un dossier pour classer votre favoris :
        </div>
        <div>
            {#each bookmarks as bookmark (bookmark.id)}
                <button
                    on:click|preventDefault={() => edit({ bookmarkId: bookmark.id })}
                    class={'btn-reset w-full cursor-pointer flex items-center border-0 border-b border-gray-200 hover:bg-gray-100 p-2'}
                >
                    <i class={'fa fa-folder fa-lg mr-2'} />
                    <span class={'flex-grow flex items-center justify-between'}>
                        <span
                            class={cx(
                                bookmark.name === bookmarkItem.bookmark.name
                                    ? 'font-bold'
                                    : '',
                            )}
                        >
                            {bookmark.isDefault ? 'Défaut' : bookmark.name}
                        </span>
                    </span>
                </button>
            {/each}
            <div
                class={'mt-1 bg-gray-100 border-b-2 border-gray-300 focus-within:border-brand-blue py-3 px-2 text-black flex items-center'}
            >
                <i class={'fa fa-folder fa-lg mr-2'} />
                <input
                    bind:value={bookmarkName}
                    class={'focus:outline-none bg-transparent flex-grow border-none'}
                    on:keypress={(event) => {
                        if (event.key === 'Enter') {
                            edit({ bookmarkName });
                        }
                    }}
                    type="text"
                    placeholder={'Nouveau dossier'}
                />
                {#if bookmarkName.trim() !== ''}
                    <button
                        type={'button'}
                        on:click={() => edit({ bookmarkName })}
                        class={'bg-transparent hover:bg-gray-200 cursor-pointer p-0 m-0 focus:outline-none border-none'}
                    >
                        <i class={'fa fa-check fa-lg'} />
                    </button>
                {/if}
            </div>
        </div>
    </div>
</Modal>
